import React, { useState } from 'react';
import { Modal, Button, StatusBadge, ActionBar } from '@/components/common';
import { PlayerAvatar } from '@/components/common/PlayerAvatar';

export interface PlayerDetailsData {
  userId: string;
  username: string;
  avatar?: string;
  role: string;
  isLocal: boolean;
}

interface PlayerDetailsModalProps {
  isOpen: boolean;
  playerData: PlayerDetailsData | null;
  onClose: () => void;
  onRespawn?: (userId: string) => Promise<void>;
}

export const PlayerDetailsModal: React.FC<PlayerDetailsModalProps> = ({
  isOpen,
  playerData,
  onClose,
  onRespawn
}) => {
  const [isRespawning, setIsRespawning] = useState(false);

  const handleRespawn = async () => {
    if (!playerData || !onRespawn || !playerData.isLocal) return;
    
    setIsRespawning(true);
    try {
      await onRespawn(playerData.userId);
    } catch (error) {
      console.error('Failed to respawn player:', error);
    } finally {
      setIsRespawning(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Player Details" size="md">
      <div className="py-5 flex flex-col gap-4">
        {playerData ? (
          <>
            <div className="flex items-center gap-4">
              <PlayerAvatar 
                username={playerData.username} 
                avatar={playerData.avatar} 
                size="lg" 
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium text-lg">
                    {playerData.username}
                  </span>
                  {playerData.isLocal && (
                    <span className="text-game-golden text-xs font-semibold italic">(You)</span>
                  )}
                </div>
                <div className="mt-2">
                  <StatusBadge type="role" value={playerData.role || 'member'} size="md" />
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-400 italic">No player data available</p>
          </div>
        )}
      </div>
      
      <ActionBar align="right" className="pt-4 border-t border-game-medium/30">
        {playerData?.isLocal && onRespawn && (
          <Button 
            variant="primary"
            onClick={handleRespawn}
            disabled={isRespawning}
            isLoading={isRespawning}
            className="px-6"
          >
            Respawn
          </Button>
        )}
        <Button 
          variant="secondary"
          onClick={onClose}
          className="px-6"
        >
          Close
        </Button>
      </ActionBar>
    </Modal>
  );
};